import React, { useEffect, useState } from "react";
import { useNoIndex } from "@/lib/usePageSeo";
import { trackEvent } from "@/lib/analytics";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { money, usdApprox, usdEquivalent, useCurrency, formatAwgExact } from "@/lib/currency";
import { CheckCircle2, ArrowRight, AlertTriangle } from "lucide-react";

// Truthful lifecycle labels — no "paid" text ever appears unless
// payment_status is literally `paid`. Test-mode surfaces are called out
// unambiguously so a public visitor can never mistake a mock completion
// for a verified real transaction.
const ORDER_STATUS_LABELS = {
  received: "Order received",
  in_production: "In production",
  completed: "Completed",
  cancelled: "Cancelled",
};

const PAYMENT_STATUS = {
  pending:    { label: "Awaiting payment", tone: "warn" },
  test_mock:  { label: "Test Mode — No Payment Received", tone: "warn" },
  authorized: { label: "Authorized",  tone: "ok" },
  paid:       { label: "Paid",        tone: "ok" },
  failed:     { label: "Payment failed", tone: "error" },
  refunded:   { label: "Refunded",    tone: "muted" },
};

export default function CheckoutResultPage({ status = "success" }) {
  useCurrency(); // re-render prices on display-currency change
  useNoIndex();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (status === "success" && order?.total) {
      trackEvent("order_complete", { value: order.total, transaction_id: order.id });
    }
  }, [status, order?.total]);
  const [params] = useSearchParams();
  const orderId = params.get("order_id");

  useEffect(() => {
    if (!orderId) return;
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [orderId]);

  const ok = status === "success";
  const paymentStatus = order?.payment_status || "pending";
  const orderStatusLabel = ORDER_STATUS_LABELS[order?.status] || (order?.status || "Received");
  const payment = PAYMENT_STATUS[paymentStatus] || { label: paymentStatus, tone: "muted" };
  const isTestMode = paymentStatus === "test_mock";

  // Heading text is written from ORDER perspective, never payment. A
  // customer whose payment failed must not see "Thank you for your order"
  // even though the record was created.
  const headline = ok
    ? (isTestMode
        ? "Order received — test mode"
        : (paymentStatus === "paid" ? "Thank you for your order!" : "Order received"))
    : "Payment Cancelled";

  const paymentToneClass = payment.tone === "ok"
    ? "text-emerald-400"
    : payment.tone === "error"
      ? "text-red-400"
      : payment.tone === "muted"
        ? "text-[#A3A3A3]"
        : "text-amber-300";

  return (
    <div data-testid="checkout-result" className="bg-[#0F0F0F] min-h-[60vh]">
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-24">
        {isTestMode && (
          <div
            data-testid="checkout-result-test-banner"
            className="mb-8 rounded-lg border border-amber-500/50 bg-amber-500/10 px-5 py-4 text-amber-200 flex items-start gap-3"
            role="alert"
          >
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.24em]">Test Mode</div>
              <div className="mt-1 text-sm font-semibold">
                No payment was received. This was a test transaction — Ur Designs will not treat this as a paid order.
              </div>
            </div>
          </div>
        )}
        <div className="text-center">
          <div className={`w-20 h-20 rounded-full mx-auto grid place-items-center ${ok ? "bg-[#FF6600]/20 border border-[#FF6600]" : "bg-red-500/20 border border-red-500"}`}>
            <CheckCircle2 className={`w-9 h-9 ${ok ? "text-[#FF6600]" : "text-red-500"}`} />
          </div>
          <h1
            className="font-display mt-6 text-4xl sm:text-5xl font-black tracking-tight"
            data-testid="checkout-result-headline"
          >
            {headline}
          </h1>
          <p className="mt-4 text-[#B5B5B5]">
            {ok
              ? "We've received your order. Our team will review the details and contact you if anything else is needed before production begins."
              : "No worries — your cart is saved if you'd like to try again."}
          </p>
        </div>
        {order && (
          <div className="mt-8 ur-card p-6" data-testid="checkout-result-card">
            <div className="text-xs uppercase tracking-widest text-[#A3A3A3]">Order ID</div>
            <div className="font-mono text-sm mt-1" data-testid="checkout-result-order-id">{order.id}</div>
            {/* Sep 2026 — full breakdown; the AWG amount actually
                charged (incl. tax) is the authoritative figure. Legacy
                orders without a tax breakdown fall back to subtotal. */}
            {(() => {
              const subtotal = Number(order.subtotal || 0);
              const total = order.total != null ? Number(order.total) : subtotal;
              const taxAmount = order.tax_amount != null ? Number(order.tax_amount) : total - subtotal;
              const taxLabel = order.tax_label || "Tax";
              return (
                <div className="mt-5 space-y-2">
                  <div className="flex items-end justify-between">
                    <span className="text-[#A3A3A3] text-sm">Subtotal</span>
                    <span className="text-sm font-semibold" data-testid="checkout-result-subtotal">{money(subtotal)}</span>
                  </div>
                  <div className="flex items-end justify-between">
                    <span className="text-[#A3A3A3] text-sm" data-testid="checkout-result-tax-label">{taxLabel}</span>
                    <span className="text-sm font-semibold" data-testid="checkout-result-tax-amount">{money(taxAmount)}</span>
                  </div>
                  <div className="flex items-end justify-between pt-2 border-t border-[#262626]">
                    <span className="text-[#A3A3A3]">Total charged</span>
                    <div className="text-right">
                      <span className="font-display text-2xl font-black block leading-none" data-testid="checkout-result-total">{formatAwgExact(total)}</span>
                      <span className="text-[11px] text-[#A3A3A3] mt-1 inline-block">{usdEquivalent(total)}</span>
                    </div>
                  </div>
                </div>
              );
            })()}
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              <div className="rounded-md border border-[#262626] px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Order Status</div>
                <div className="text-white mt-1 text-sm font-semibold" data-testid="checkout-result-order-status">{orderStatusLabel}</div>
              </div>
              <div className="rounded-md border border-[#262626] px-4 py-3">
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Payment</div>
                <div className={`mt-1 text-sm font-semibold ${paymentToneClass}`} data-testid="checkout-result-payment-status">{payment.label}</div>
              </div>
            </div>
          </div>
        )}
        <div className="text-center">
          <Link to="/" className="ur-btn-primary mt-10" data-testid="checkout-result-home">
            Back to Home <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
